/**
 * Public NGO Partner Page — /partners/:slug
 *
 * The "member-facing" page for an NGO partner. If the NGO doesn't want to
 * manage a page themselves, this is the default: their info (logo, tagline,
 * contact) + a big Claim-my-free-year CTA that routes to /join/:slug.
 *
 * If a partner has managers, we surface a "Manage this page" link that only
 * they can act on (auth-gated in the API).
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import SEO from '../components/SEO';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import {
  Heart,
  Loader2,
  Globe,
  MapPin,
  Phone,
  Mail,
  Gift,
  ArrowRight,
  BarChart3,
  Users,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PartnerPublicPage() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/ngos/${slug}`);
        if (!cancel) setPartner(res.data);
      } catch {
        if (!cancel) setNotFound(true);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [slug]);

  const isManager =
    user &&
    partner &&
    (user.is_admin ||
      (partner.manager_emails || [])
        .map((s) => s.toLowerCase())
        .includes((user.email || '').toLowerCase()));

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center" data-testid="partner-loading">
        <Loader2 className="h-6 w-6 animate-spin text-amber-500" />
      </div>
    );
  }

  if (notFound || !partner) {
    return (
      <div className="max-w-md mx-auto py-16 px-4 text-center" data-testid="partner-not-found">
        <h1 className="text-3xl font-bold mb-3">Partner not found</h1>
        <p className="text-muted-foreground mb-6">
          This partner page isn't active. Head back home or check the URL.
        </p>
        <Button asChild>
          <Link to="/">Go to the Club</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white" data-testid="partner-page">
      <SEO
        title={`${partner.name} — an NGO partner of the American Dog Club`}
        description={partner.tagline || `${partner.name} is a partner of the American Dog Club.`}
      />
      <div className="max-w-4xl mx-auto px-4 py-10">
        {/* Hero */}
        <div className="text-center mb-8">
          {partner.logo_url ? (
            <img
              src={partner.logo_url}
              alt={partner.name}
              className="h-24 w-auto mx-auto mb-4 rounded"
            />
          ) : (
            <div className="h-20 w-20 mx-auto mb-4 rounded-full bg-amber-100 flex items-center justify-center">
              <Heart className="h-10 w-10 text-amber-600" />
            </div>
          )}
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight text-slate-900 mb-3">
            {partner.name}
          </h1>
          {partner.tagline && (
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">{partner.tagline}</p>
          )}
        </div>

        {/* Big CTA */}
        <Card className="border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50 mb-6">
          <CardContent className="p-6 md:p-8">
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <div className="flex-1">
                <div className="inline-flex items-center gap-2 rounded-full bg-white/70 px-3 py-1 text-xs font-semibold text-amber-800 mb-3">
                  <Gift className="h-3.5 w-3.5" />
                  New here? {partner.name} gives you a free year
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-1">
                  Claim your 12-month full membership
                </h2>
                <p className="text-sm text-slate-700">
                  {partner.name} partners with the American Dog Club to gift new families a courtesy year. No card required.
                </p>
              </div>
              <Button
                asChild
                size="lg"
                className="bg-amber-500 hover:bg-amber-600"
                data-testid="partner-claim-cta"
              >
                <Link to={`/join/${slug}`}>
                  Claim my free year
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="grid md:grid-cols-2 gap-4">
          {/* Contact card */}
          <Card>
            <CardContent className="p-5">
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">
                About {partner.name}
              </p>
              <div className="space-y-2 text-sm text-slate-700">
                {partner.address && (
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
                    <span>{partner.address}</span>
                  </div>
                )}
                {partner.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 shrink-0 text-amber-600" />
                    <span>{partner.phone}</span>
                  </div>
                )}
                {partner.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 shrink-0 text-amber-600" />
                    <a href={`mailto:${partner.email}`} className="hover:underline">
                      {partner.email}
                    </a>
                  </div>
                )}
                {partner.website && (
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4 shrink-0 text-amber-600" />
                    <a
                      href={partner.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline break-all"
                    >
                      {partner.website.replace(/^https?:\/\//, '')}
                    </a>
                  </div>
                )}
                {!partner.address && !partner.phone && !partner.email && !partner.website && (
                  <p className="text-muted-foreground italic">
                    Contact info not shared yet. Watch this space.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Impact card (public read of just the count) */}
          <Card>
            <CardContent className="p-5">
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">
                Impact
              </p>
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700">
                  <Users className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-2xl font-black text-slate-900" data-testid="partner-signups-count">
                    {partner.signups_count?.toLocaleString('en-US') || 0}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    families joined the club through {partner.name}
                  </p>
                </div>
              </div>
              {isManager && (
                <Button
                  asChild
                  variant="outline"
                  size="sm"
                  className="mt-4 w-full"
                  data-testid="partner-manage-link"
                >
                  <Link to={`/ngo/manage/${slug}`}>
                    <BarChart3 className="mr-2 h-4 w-4" />
                    Open impact dashboard
                  </Link>
                </Button>
              )}
            </CardContent>
          </Card>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-6">
          Want to bring your own community in?{' '}
          <Link to="/ngo/join" className="underline">Apply to become a partner</Link>
        </p>
      </div>
    </div>
  );
}
